import { omit as _omit, orderBy as _orderBy, sortBy as _sortBy } from "lodash"
import Constant from "../../../constant"

const convertEvaluationScoreType = (evalScore) => {
   if (evalScore === Constant.EVALUATION_SCORE.ADMINSITRATION_VALUE) {
      return "administrationScore"
   } else if (evalScore === Constant.EVALUATION_SCORE.PRICE_VALUE) {
      return "priceScore"
   } else if (evalScore === Constant.EVALUATION_SCORE.TECHNICAL_VALUE) {
      return "technicalScore"
   }
   return null
}

const hasPriceEvaluationRank = (vendor) => typeof vendor?.priceEvaluationRank === "number"

// A full tie follows the rank frozen at Price Evaluation (PM494), as PROMISE does when picking the winner.
const byPriceEvaluationRank = (vendor) => (hasPriceEvaluationRank(vendor) ? vendor.priceEvaluationRank : Infinity)

const sortEvaluationScore = (procurement, procurementVendor) => {
   let orderResult
   orderResult = []
   if (procurement.evaluationMethod === Constant.EVALUATION_METHODS.QUALITY_SYSTEM) {
      // totalScore is never written for Quality System (only updateMeritPointScore computes it), so
      // price is the tie-break; vendors that never offered one rank behind those that did.
      const sortFields = ["technicalScore", (vendor) => (vendor.lastPrice > 0 ? 0 : 1), "lastPrice", byPriceEvaluationRank, "lastBidTime", "name"]
      const sortOrders = ["desc", "asc", "asc", "asc", "asc", "asc"]
      orderResult = _orderBy(procurementVendor, sortFields, sortOrders)
   } else if (procurement.evaluationMethod === Constant.EVALUATION_METHODS.MERIT_POINT) {
      const evalScore = _omit(procurement.evaluationScoring, "_id")
      const sortEvalScore = []
      for (const x in evalScore) {
         sortEvalScore.push([x, evalScore[x]])
      }
      sortEvalScore.sort(function (a, b) {
         return b[1] - a[1]
      })
      const keyScore = convertEvaluationScoreType(sortEvalScore[0][0])

      // totalScore is rounded to 2dp before it is stored, so near-equal offers tie on it; price is the
      // tie-break, and vendors that never offered one rank behind those that did.
      const meritSortFields = ["totalScore", (vendor) => (vendor.lastPrice > 0 ? 0 : 1), "lastPrice", byPriceEvaluationRank, "lastBidTime", keyScore, "name"]
      const meritSortOrders = ["desc", "asc", "asc", "asc", "asc", "desc", "asc"]

      // find vendor with minus saving
      const minusSavingVendors = procurementVendor.filter((vendor) => vendor.saving < 0)

      if (procurementVendor.length > 2) {
         // do this condition only if auction participant is more than 2 vendors
         // this case is for closed auction

         if (minusSavingVendors.length > 0) {
            // 1. filter and order vendors without minus saving
            const orderVendorWithoutMinusSaving = _orderBy(procurementVendor, meritSortFields, meritSortOrders).filter(
               (vendor) => vendor.saving >= 0,
            )

            // 2. filter and order only for minus saving vendor
            const orderVendorMinusSaving = _orderBy(minusSavingVendors, meritSortFields, meritSortOrders)

            // 3. merge point 1 & 2
            orderResult = [...orderVendorWithoutMinusSaving, ...orderVendorMinusSaving]
         } else {
            // if no minus saving vendors
            orderResult = _orderBy(procurementVendor, meritSortFields, meritSortOrders)
         }
      } else { // if only 2 vendors
         if (minusSavingVendors.length > 0) {
            // if there are one or two vendors with minus saving
            const vendorWithoutMinusSaving = procurementVendor.filter((vendor) => vendor.saving >= 0)

            // put vendor without minus saving on top list and the rest is for minus saving
            orderResult = [...vendorWithoutMinusSaving, ...minusSavingVendors]
         } else {
            orderResult = _orderBy(procurementVendor, meritSortFields, meritSortOrders)
         }
      }
   } else if (procurement.evaluationMethod !== Constant.EVALUATION_METHODS.MERIT_POINT) {
      const _procurementVendor = procurementVendor.map((vendor) => {
         if (vendor.lastBidTime === null) {
            vendor.lastBidTime = new Date(9999, 11, 31, 23, 59, 59).getTime()
         } else {
            vendor.lastBidTime = new Date(vendor.lastBidTime).getTime()
         }
         return vendor
      })

      const includeZeroBid = _procurementVendor.find((v) => v.lastPrice === 0)
      const bidOverOneVendor = _procurementVendor.filter((v) => v.lastPrice > 0).length > 1
      if (bidOverOneVendor && includeZeroBid) {
         // process for if more than one vendor bid exist and another has not already bid

         if (procurementVendor.length > 2) { // if more than two vendors
            const minusSavingVendors = _procurementVendor.filter((vendor) => vendor.saving < 0)
            if (minusSavingVendors.length > 0) { // if there are 1 or 2 vendor with minus saving
               // 1. find vendor without minus saving and 0 bid
               const vendorWithoutMinusSavingAndZeroBid = _procurementVendor.filter(
                  (v) => v.saving >= 0 && v.lastPrice > 0,
               )

               // 2. order vendor without minus saving and 0 bid
               const orderVendorWithoutMinusSavingAndZeroBid = _orderBy(
                  vendorWithoutMinusSavingAndZeroBid,
                  ["lastPrice", byPriceEvaluationRank, "lastBidTime", "name"],
                  ["asc", "asc", "asc", "asc"],
               )

               // 3. filter zero bid
               const _zeroBid = _procurementVendor.filter((v) => v.lastPrice == 0)

               // 4. order vendors with minus saving
               const orderVendorMinusSaving = _orderBy(minusSavingVendors, ["lastPrice", byPriceEvaluationRank, "lastBidTime", "name"], ["asc", "asc", "asc", "asc"])

               // 5. merge and put vendor without minus saving and 0 bid at the top list array with zero at the middle and minus on the rest
               orderResult = [...orderVendorWithoutMinusSavingAndZeroBid, ..._zeroBid, ...orderVendorMinusSaving]
            } else { // if
               // sort from lowest last price & remove vendor with 0 last price
               const newOrderResult = _orderBy(_procurementVendor, ["lastPrice"], ["asc"]).filter(
                  (v) => v.lastPrice > 0,
               )

               const _noZeroBid = _orderBy(newOrderResult, ["lastPrice", byPriceEvaluationRank, "lastBidTime"], ["asc", "asc", "asc"])
               const _zeroBid = _procurementVendor.filter((v) => v.lastPrice == 0)

               orderResult = [..._noZeroBid, ..._zeroBid]
            }
         } else if (procurementVendor.length < 3) { // if only 2 vendors
            orderResult = _orderBy(_procurementVendor, ["saving", byPriceEvaluationRank, "lastBidTime", "name"], ["desc", "asc", "asc", "asc"])
         }
      } else if (includeZeroBid) {
         // process for if only just one bid exist

         const minusSavingVendors = _procurementVendor.filter((vendor) => vendor.saving < 0) // get vendors with minus saving
         if (procurementVendor.length > 2) { // for more than 2 vendors
            if (minusSavingVendors.length > 0) { // if there are vendors with minus saving
               // 1. filter and order vendors without minus saving
               const orderVendorWithoutMinusSaving = _orderBy(
                  _procurementVendor,
                  ["lastPrice", byPriceEvaluationRank, "lastBidTime", "name"],
                  ["asc", "asc", "asc", "asc"],
               ).filter((v) => v.saving >= 0)

               // 2. filter and order only for minus vendors
               const orderVendorMinusSaving = _orderBy(minusSavingVendors, ["lastPrice", byPriceEvaluationRank, "lastBidTime", "name"], ["asc", "asc", "asc", "asc"])

               // 3. merge and place order vendors without minus saving above order vendors with minus saving
               orderResult = [...orderVendorWithoutMinusSaving, ...orderVendorMinusSaving]
            } else { // if no vendors with minus saving
               orderResult = _orderBy(_procurementVendor, ["lastPrice", byPriceEvaluationRank, "lastBidTime", "name"], ["desc", "asc", "asc", "asc"])
            }
         } else {
            if (minusSavingVendors.length > 0) { // for only two vendors which one of them has minus saving
               const vendorWithoutMinusSaving = _procurementVendor.filter((vendor) => vendor.saving >= 0)

               // put vendor without minus saving on top list and the rest is for minus saving
               orderResult = [...vendorWithoutMinusSaving, ...minusSavingVendors]
            } else { // if no minus saving with two vendors
               orderResult = _orderBy(
                  _procurementVendor,
                  ["saving", "lastPrice", byPriceEvaluationRank, "lastBidTime", "name"],
                  ["desc", "asc", "asc", "asc", "asc"],
               )
            }
         }
      } else {
         // process if all vendor already bid
         orderResult = _orderBy(_procurementVendor, ["saving", "lastPrice", byPriceEvaluationRank, "lastBidTime", "name"], ["desc", "asc", "asc", "asc", "asc"])
      }
   }
   // No branch above sinks an over-ceiling bid, so do it once here for all of them.
   return [...orderResult.filter((v) => !v.isAboveCeiling), ...orderResult.filter((v) => v.isAboveCeiling)]
}

const searchInAuctionSession = ({ procurementVendors, query }) => {
   return procurementVendors.filter((procurementVendor) => {
      const lowerCaseQuery = query.toLowerCase()
      return (
         procurementVendor.name.toLowerCase().includes(lowerCaseQuery) ||
         procurementVendor.attendanceStatus.toLowerCase().includes(lowerCaseQuery) ||
         (procurementVendor.administrationPassed && "passed".includes(lowerCaseQuery)) ||
         (!procurementVendor.administrationPassed && "not passed".includes(lowerCaseQuery)) ||
         procurementVendor.technicalScore.toString().includes(lowerCaseQuery) ||
         procurementVendor.priceScore.toString().includes(lowerCaseQuery) ||
         procurementVendor.totalScore.toString().includes(lowerCaseQuery) ||
         procurementVendor.indicatorPrice.toString().includes(lowerCaseQuery) ||
         procurementVendor.lastPrice.toString().includes(lowerCaseQuery)
      )
   })
}

// Rank is fixed at Price Evaluation (PM494); procurements pushed before it carry no rank and keep their order.
const sortByPriceEvaluationRank = (vendors) => {
   if (!vendors?.length || !vendors.every(hasPriceEvaluationRank)) return vendors
   return _sortBy(vendors, "priceEvaluationRank")
}

const displayRank = (vendor, index) => vendor?.priceEvaluationRank ?? index + 1

const ProcurementVendorUtil = {
   sortEvaluationScore,
   searchInAuctionSession,
   sortByPriceEvaluationRank,
   displayRank,
}

export { sortByPriceEvaluationRank, displayRank }

export default ProcurementVendorUtil
